import { getRouteApi, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { API_BASE } from "@/lib/api";
import { track } from "@/lib/analytics";
import { getPublicConfig } from "@/lib/public-config";
import { cn } from "@/lib/utils";
import "./support.css";

const routeApi = getRouteApi("/request-race");

const TURNSTILE_SCRIPT_ID = "turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        },
      ) => string;
      reset: (widgetId: string) => void;
    };
  }
}

type SubmitState = "idle" | "submitting" | "success" | "error";

function ensureTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Turnstile can only run in the browser"));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(
    TURNSTILE_SCRIPT_ID,
  ) as HTMLScriptElement | null;
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load Turnstile script")),
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(script);
  });
}

export default function RequestRacePage() {
  const search = routeApi.useSearch();
  const source = search.source ?? "direct";
  const initialRaceName = search.race ?? "";

  const [revealed, setRevealed] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [raceName, setRaceName] = useState(initialRaceName);
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  useEffect(() => {
    setRaceName(initialRaceName);
  }, [initialRaceName]);

  useEffect(() => {
    const config = getPublicConfig();
    setTurnstileSiteKey(config.turnstileSiteKey);
    setConfigLoaded(true);
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) {
      return;
    }

    let cancelled = false;

    ensureTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileContainerRef.current || !window.turnstile) {
          return;
        }
        if (turnstileWidgetIdRef.current) {
          return;
        }

        turnstileWidgetIdRef.current = window.turnstile.render(
          turnstileContainerRef.current,
          {
            sitekey: turnstileSiteKey,
            theme: "dark",
            callback: (token: string) => {
              setTurnstileToken(token);
              setTurnstileReady(true);
            },
            "expired-callback": () => {
              setTurnstileToken("");
              setTurnstileReady(false);
            },
            "error-callback": () => {
              setTurnstileToken("");
              setTurnstileReady(false);
            },
          },
        );
      })
      .catch(() => {
        if (!cancelled) {
          setTurnstileReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [turnstileSiteKey]);

  const submitDisabled =
    submitState === "submitting" ||
    !raceName.trim() ||
    !email.trim() ||
    !turnstileToken ||
    !turnstileSiteKey ||
    !turnstileReady;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitDisabled) return;

    setSubmitState("submitting");
    setSubmitError(null);
    setSubmitMessage(null);

    const trimmedRaceName = raceName.trim();
    const trimmedEmail = email.trim();
    const sourcePath =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : undefined;

    track("tr_race_request_submit_attempt", {
      source,
      has_email: Boolean(trimmedEmail),
      race_name: trimmedRaceName,
      search_query: initialRaceName || null,
    });

    try {
      const response = await fetch(`${API_BASE}/support/race-request`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          raceName: trimmedRaceName,
          email: trimmedEmail,
          turnstileToken,
          sourcePath,
          searchQuery: initialRaceName || trimmedRaceName,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { success?: boolean; message?: string }
        | null;

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Race request failed.");
      }

      setSubmitState("success");
      setEmail("");
      setTurnstileToken("");
      setTurnstileReady(false);
      setSubmitMessage(
        "Request sent. We'll email you if we need more details.",
      );
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }

      track("tr_race_request_submit_success", {
        source,
        race_name: trimmedRaceName,
        search_query: initialRaceName || null,
      });
    } catch (error) {
      setSubmitState("error");
      setSubmitError(
        error instanceof Error ? error.message : "Race request failed.",
      );
      setTurnstileToken("");
      setTurnstileReady(false);
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }

      track("tr_race_request_submit_error", {
        source,
        race_name: trimmedRaceName,
        search_query: initialRaceName || null,
        reason: error instanceof Error ? error.message : "unknown",
      });
    }
  }

  return (
    <div className="support-page">
      <section className={cn("support-shell", revealed && "revealed")}>
        <Link
          to="/records"
          search={{ type: "events", q: initialRaceName || undefined }}
          className="support-back-link"
        >
          Back to search
        </Link>
        <p className="support-eyebrow">MISSING RACE</p>
        <h1 className="support-title">Request a race</h1>
        <p className="support-subtitle">
          Tell us the race name and where to reach you.
        </p>

        <form className="support-form" onSubmit={handleSubmit}>
          <label className="support-label" htmlFor="race-request-name">
            Race name
          </label>
          <input
            id="race-request-name"
            type="text"
            className="support-input"
            autoComplete="off"
            maxLength={255}
            value={raceName}
            onChange={(event) => setRaceName(event.target.value)}
            required
          />

          <label className="support-label" htmlFor="race-request-email">
            Your email
          </label>
          <input
            id="race-request-email"
            type="email"
            className="support-input"
            autoComplete="email"
            maxLength={320}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />

          {!turnstileSiteKey && configLoaded ? (
            <div className="support-warning">
              Captcha is not configured. Set `BUN_PUBLIC_TURNSTILE_SITE_KEY`.
            </div>
          ) : turnstileSiteKey ? (
            <div className="support-captcha-wrap">
              <div ref={turnstileContainerRef} className="support-captcha" />
            </div>
          ) : null}

          {submitError ? <div className="support-error">{submitError}</div> : null}
          {submitMessage ? (
            <div className="support-success">{submitMessage}</div>
          ) : null}

          <button type="submit" className="support-submit" disabled={submitDisabled}>
            {submitState === "submitting" ? "Sending..." : "Send request"}
          </button>
        </form>
      </section>
    </div>
  );
}
